/** Host-native renderer for the Elevate v1 semantic contract (#1465). */
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { elevateDirectionLabel, elevateRenderIssues, type ElevateAction, type ElevateItem as GovernedElevateItem } from '@solum/experience'
import { readableError } from '@solum/format'
import { Badge, Button, EmptyState, Pill } from '../controls'
import {
  CalendarDays, Check, CheckSquare2, ExternalLink, FileText, History, Mail,
  MailOpen, MessageCircle, MoreHorizontal, Phone, Pin, StickyNote, Users,
  type LucideIcon,
} from 'lucide-react'
import s from './ElevateCardList.module.css'

export interface ElevateCardGroup {
  id: string
  label?: ReactNode
  items: readonly ElevateItem[]
}

export interface ElevateCardListProps {
  groups: readonly ElevateCardGroup[]
  empty: ReactNode
  locale?: string
  testId?: string
  /**
   * The semantic action ids `onAction` can actually route. Omitted means the
   * host makes no reach claim and only structure is checked; supplying it makes
   * an adapter/renderer disagreement visible here instead of on a press.
   */
  supports?: readonly string[]
  /**
   * The search the list is showing the result of, so a matched card can SAY
   * where it matched. Highlighting is done from the same query the filter used;
   * a card highlighted for one reason and matched for another is worse than no
   * highlight at all.
   */
  query?: string
  /** Injected so a relative time is testable without freezing the clock. */
  now?: Date
  onAction: (item: ElevateItem, action: Extract<ElevateAction, { state: 'available' }>) => Promise<void> | void
}

function when(item: ElevateItem, locale: string) {
  const raw = item.time.occurredAt ?? item.time.dueAt ?? item.time.effectiveAt
  const value = new Date(raw)
  return Number.isNaN(value.getTime()) ? raw : new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: raw.includes('T') ? 'short' : undefined,
  }).format(value)
}

/**
 * How long ago, in the reader's own language.
 *
 * A timeline is read for recency and "12 Mar" makes the eye do arithmetic the
 * machine can do. The exact stamp is not lost: it stays as the element's
 * `title`, so the precise value is one hover or one accessible-name read away.
 * `now` is injected rather than read from the clock so this is testable without
 * freezing time.
 */
export function relativeWhen(item: ElevateItem, locale: string, now: Date): string | null {
  const raw = item.time.occurredAt ?? item.time.dueAt ?? item.time.effectiveAt
  const value = new Date(raw)
  if (Number.isNaN(value.getTime())) return null
  const seconds = Math.round((value.getTime() - now.getTime()) / 1000)
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const units: readonly [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000], ['month', 2592000], ['week', 604800],
    ['day', 86400], ['hour', 3600], ['minute', 60],
  ]
  const magnitude = Math.abs(seconds)
  for (const [unit, size] of units) {
    if (magnitude >= size) return format.format(Math.trunc(seconds / size), unit)
  }
  return format.format(0, 'minute')
}

/**
 * The query's matches, marked.
 *
 * Returns SEGMENTS rather than markup so the caller decides the element and no
 * component here builds HTML from a value it did not author. Matching is
 * case-insensitive on the same basis the filter uses, so a card cannot be
 * highlighted for a reason it was not matched for -- or matched without being
 * highlighted, which is the more confusing of the two.
 */
export function highlightSegments(value: string, query: string): { text: string; match: boolean }[] {
  const needle = query.trim().toLocaleLowerCase()
  if (!needle) return [{ text: value, match: false }]
  const haystack = value.toLocaleLowerCase()
  const segments: { text: string; match: boolean }[] = []
  let cursor = 0
  for (;;) {
    const at = haystack.indexOf(needle, cursor)
    if (at < 0) break
    if (at > cursor) segments.push({ text: value.slice(cursor, at), match: false })
    segments.push({ text: value.slice(at, at + needle.length), match: true })
    cursor = at + needle.length
  }
  if (segments.length === 0) return [{ text: value, match: false }]
  if (cursor < value.length) segments.push({ text: value.slice(cursor), match: false })
  return segments
}

function Highlight({ value, query }: { value: string; query: string }) {
  const segments = highlightSegments(value, query)
  if (segments.length === 1 && !segments[0]?.match) return <>{value}</>
  return <>{segments.map((segment, index) => (segment.match
    ? <mark key={index}>{segment.text}</mark>
    : <span key={index}>{segment.text}</span>))}</>
}

const ITEM_ICON = {
  email: Mail, note: StickyNote, phone: Phone, call: Phone, task: CheckSquare2,
  appointment: CalendarDays, meeting: Users, whatsapp: MessageCircle,
  audit: History, work: CheckSquare2,
} as const
const ACTION_ICON: Record<string, LucideIcon> = {
  open: ExternalLink, complete: Check, pin: Pin, 'read-toggle': MailOpen,
}

function ElevateCard({
  item,
  issues,
  locale,
  query,
  now,
  tabIndex,
  cardRef,
  onFocus,
  onMove,
  onAction,
}: {
  item: ElevateItem
  issues: readonly string[]
  locale: string
  query: string
  now: Date
  tabIndex: number
  cardRef: (element: HTMLButtonElement | null) => void
  onFocus: () => void
  onMove: (event: KeyboardEvent<HTMLButtonElement>) => void
  onAction: ElevateCardListProps['onAction']
}) {
  const detailsId = useId()
  const summaryRef = useRef<HTMLButtonElement | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const conformant = issues.length === 0
  const available = conformant
    ? item.actions.filter((action): action is Extract<ElevateAction, { state: 'available' }> => action.state === 'available')
    : []
  const refused = item.actions.filter((action): action is Extract<ElevateAction, { state: 'refused' }> => action.state === 'refused')
  const owner = item.actors.find((actor) => actor.role === 'owner' || actor.role === 'assignee')
  const visualKind = (item.channel ?? item.kind).toLowerCase()
  const ItemIcon = ITEM_ICON[visualKind as keyof typeof ITEM_ICON] ?? FileText

  const act = async (action: Extract<ElevateAction, { state: 'available' }>) => {
    setBusy(action.id); setError(null)
    try { await onAction(item, action) }
    catch (caught) {
      setError(readableError(caught, 'The action could not be completed.'))
      setExpanded(true)
    }
    finally { setBusy(null) }
  }

  /* On the CARD, not on the summary button: the disclosure is the button's
   * SIBLING, so an Escape pressed while focus is inside the details never
   * bubbles through the button. Written there first, and the test that presses
   * a control inside the disclosure is what found it. Only when nothing is
   * open does Escape belong to the shell above. */
  const escape = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || !expanded) return
    event.preventDefault()
    event.stopPropagation()
    setExpanded(false)
    summaryRef.current?.focus()
  }

  return (
    <article
      className={s.card}
      data-kind={item.kind}
      data-channel={visualKind}
      data-unread={item.unread || undefined}
      data-source={item.source.id}
      data-conformant={conformant ? undefined : 'false'}
      data-testid="elevate-card"
      onKeyDown={escape}
    >
      <button
        ref={(element) => { summaryRef.current = element; cardRef(element) }}
        type="button"
        className={s.summaryButton}
        /* The card carries two expandable controls now — this disclosure and
           the action menu, which correctly declares `aria-expanded` for its
           `aria-haspopup="menu"`. "The button that is not expanded" stopped
           identifying either of them, so the disclosure is named. */
        data-testid="elevate-card-summary"
        tabIndex={tabIndex}
        aria-expanded={expanded}
        aria-controls={detailsId}
        onFocus={onFocus}
        onKeyDown={onMove}
        onClick={() => setExpanded((value) => !value)}
      >
        <span className={s.topline}>
          <span className={s.kindIcon} aria-hidden="true"><ItemIcon /></span>
          <span className={s.kind}>{item.channel ?? item.kind}</span>
          <Badge tone="neutral">{item.source.label}</Badge>
          {item.direction && (
            <Badge tone="neutral" data-testid="elevate-card-direction">
              {elevateDirectionLabel(item.direction)}
            </Badge>
          )}
          {!conformant && <Pill tone="critical">Not shown</Pill>}
          {conformant && item.priority?.id === 'high' && <Pill tone="warn">{item.priority.label}</Pill>}
          {conformant && item.unread && <span className={s.unread}>Unread</span>}
        </span>
        <span className={s.titleLine}>
          <strong><Highlight value={item.title} query={query} /></strong>
          <Pill tone={item.sla?.state === 'breached' ? 'critical' : item.sla?.state === 'at-risk' ? 'warn' : 'neutral'}>
            {item.status.label}
          </Pill>
        </span>
        <span className={s.meta}>
          {/* Relative for reading, exact on hover and in the accessible name. */}
          <span title={when(item, locale)} data-testid="elevate-card-when">
            {relativeWhen(item, locale, now) ?? when(item, locale)}
          </span>
          {owner && <span>{owner.role === 'assignee' ? 'Assigned to' : 'Owned by'} {owner.label}</span>}
          {item.sla && <span>{item.sla.label}</span>}
        </span>
      </button>

      {available.length > 0 && (
        <div role="group" className={s.quickActions} aria-label={`Quick actions for ${item.title}`}>
          {available.slice(0, 3).map((action) => {
            const ActionIcon = ACTION_ICON[action.id] ?? MoreHorizontal
            return <button key={action.id} type="button" title={action.label}
              aria-label={`Quick action: ${action.label}`} disabled={busy != null}
              onClick={() => { void act(action) }}><ActionIcon aria-hidden="true" /></button>
          })}
        </div>
      )}

      {expanded && !conformant && (
        <div className={s.details} id={detailsId}>
          <p role="alert" className={s.error}>
            This item does not meet the Elevate contract, so its actions are withheld rather than offered and refused on press.
          </p>
          <ul className={s.issues} data-testid="elevate-card-issues">
            {issues.map((issue) => <li key={issue}>{issue}</li>)}
          </ul>
        </div>
      )}

      {expanded && conformant && (
        <div className={s.details} id={detailsId}>
          {item.summary && <p>{item.summary}</p>}
          {item.body && (item.body.redacted
            /* The door replaced the text with `[redacted]`. Printing that
               literal would put a machine's placeholder where a person's words
               belong and read as content. Marble's rule is that a withheld
               thing is NAMED as withheld, so the reader knows something exists
               and knows why they cannot see it. */
            ? <p className={s.refusal} data-testid="elevate-card-body-redacted">
                <b>Body withheld:</b> this activity reaches you through a linked
                person rather than this record, so its detail stays with the
                record it is about.
              </p>
            : <p data-testid="elevate-card-body">
                <Highlight value={item.body.text} query={query} />
              </p>)}
          {item.related.length > 0 && (
            <div role="group" className={s.related} aria-label="Related records">
              {item.related.map((record) => <Badge key={`${record.entity}:${record.id}`} tone="neutral">{record.label ?? record.entity}</Badge>)}
            </div>
          )}
          {item.affordances && (
            <div className={s.affordances}>
              {item.affordances.attachmentCount != null && <span>{item.affordances.attachmentCount} attachments</span>}
              {item.affordances.threadCount != null && <span>{item.affordances.threadCount} thread items</span>}
              {item.affordances.auditCount != null && <span>{item.affordances.auditCount} changes</span>}
            </div>
          )}
          {refused.map((action) => (
            <p key={action.id} className={s.refusal}><b>{action.label} unavailable:</b> {action.reason}</p>
          ))}
          {available.length > 0 && (
            <div role="group" className={s.actions} aria-label={`Actions for ${item.title}`}>
              {available.map((action, index) => (
                <Button
                  key={action.id}
                  size="sm"
                  variant={index === 0 ? 'primary' : 'secondary'}
                  loading={busy === action.id}
                  disabled={busy != null}
                  onClick={() => { void act(action) }}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
          {error && <p role="alert" className={s.error}>{error}</p>}
        </div>
      )}
    </article>
  )
}

export function ElevateCardList({
  groups, empty, locale = 'en', testId = 'elevate-card-list', supports, onAction,
  query = '', now,
}: ElevateCardListProps) {
  /* Read once per render rather than per card, so every card on one screen
     agrees about "now" -- and injectable, so a test does not have to freeze the
     clock to assert a relative time. */
  const asOf = now ?? new Date()
  const total = groups.reduce((sum, group) => sum + group.items.length, 0)
  const [active, setActive] = useState(0)
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  useEffect(() => {
    setActive((current) => Math.max(0, Math.min(total - 1, current)))
    refs.current.length = total
  }, [total])
  const focus = (next: number) => {
    const index = Math.max(0, Math.min(total - 1, next))
    setActive(index)
    refs.current[index]?.focus()
  }
  const move = (index: number) => (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') { event.preventDefault(); focus(index + 1) }
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') { event.preventDefault(); focus(index - 1) }
    else if (event.key === 'Home') { event.preventDefault(); focus(0) }
    else if (event.key === 'End') { event.preventDefault(); focus(total - 1) }
  }

  if (total === 0) return <EmptyState>{empty}</EmptyState>
  let index = -1
  return (
    <div role="group" className={s.list} data-testid={testId} aria-label="Elevate timeline cards">
      {groups.map((group) => (
        <section key={group.id} className={s.group} aria-labelledby={group.label != null ? `${testId}-${group.id}` : undefined}>
          {group.label != null && <h3 id={`${testId}-${group.id}`} className={s.groupLabel}>{group.label}</h3>}
          <ul>
            {group.items.map((item) => {
              index += 1
              const cardIndex = index
              return (
                <li key={`${item.source.id}:${item.id}`}>
                  <ElevateCard
                    item={item}
                    query={query}
                    now={asOf}
                    issues={elevateRenderIssues(item, supports)}
                    locale={locale}
                    tabIndex={active === cardIndex ? 0 : -1}
                    cardRef={(element) => { refs.current[cardIndex] = element }}
                    onFocus={() => setActive(cardIndex)}
                    onMove={move(cardIndex)}
                    onAction={onAction}
                  />
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}

/* Re-declared beside the component whose props are written in it, so a host
   can type the data it hands this component from `@solum/ui` alone (#2406,
   design-sync kit pass: "hosts need @solum/experience to type their data").
   An alias, not a copy — the shape stays Experience's. */
export type ElevateItem = GovernedElevateItem
